<template>
    <div class="tw:flex tw:flex-col tw:gap-4">

        <!-- ── Indicador de paso (stepper visual) ── -->
        <Stepper
            :steps="pasoLabels"
            :current-step="stepperStep"
            class="tw:px-2"
        />

        <!-- ── Timer de sesión ── -->
        <CountdownBar
            v-if="sessionEndsAt"
            :ends-at="sessionEndsAt"
            :label="$t('perfil.cambio_asiento.tiempo_restante')"
            :auto-start="true"
            :urgency-threshold-ms="60000"
            size="sm"
            class="tw:bg-white tw:border tw:border-gray-200 tw:rounded-lg tw:font-light"
            @expired="handleTimerExpirado"
        />

        <!-- ── Modal: tiempo agotado ── -->
        <BaseModalFront
            :show="showModalTiempoAgotado"
            :show-cancel="false"
            :confirm-text="$t('partido.tiempo_modal_cerrar')"
            confirm-variant="alt"
            :close-on-backdrop="false"
            :close-on-escape="false"
            @confirm="cerrarModalTiempoAgotado"
            @close="cerrarModalTiempoAgotado"
        >
            <template #header>
                <div class="tw:flex tw:items-center tw:gap-2">
                    <i class="material-icons tw:text-red-600 tw:text-2xl">timer_off</i>
                    <h2 class="tw:text-xl tw:font-semibold tw:text-red-600 tw:m-0">
                        {{ $t('perfil.cambio_asiento.tiempo_agotado_titulo') }}
                    </h2>
                </div>
            </template>
            <p class="tw:text-sm tw:text-gray-600 tw:m-0">
                {{ $t('perfil.cambio_asiento.tiempo_agotado_info') }}
            </p>
        </BaseModalFront>

        <!-- ── Contenido de cada paso (dispatcher por camino = tipo_proceso_online) ── -->

        <!--
            Dispatcher por `camino` (= `recinto.tipo_proceso_online`):
            - 'secciones' (tipo_proceso_online === 2): flow con componentes
              de master. `CambioButacaPasoZona` (SeccionesComponent +
              VenueMapCard/ZoneTableCard) + `CambioButacaPasoButaca`
              (SeatSelectionCard con SVG completo y tipo de abono por
              butaca, o LeafletComponent si tipo_svg === 2).
              El `tipo_svg` se pasa tal cual al componente.
            - 'regiones' | 'fallback-legacy' | null (tipo_proceso_online === 1
              o null): flow nuevo. Zonas planas con `VenueMapCard` +
              `ZoneTableCard`, y butacas con `SelectorAsientosComponent`
              o `LeafletComponent` según `tipo_svg`.
            - 'mapa-general' (tipo_proceso_online === 3): flow nuevo sin paso
              de zona, renderiza el mapa SVG del recinto completo.

            El `tipo_svg` es INTERNO de cada flow (Leaflet vs grid). No
            se usa en el dispatcher — el dispatcher es SOLO por `camino`.
        -->

        <!-- ── 'secciones' — flow master (componentes originales) ── -->
        <!--
            Pasamos el `tipo_svg` REAL del recinto y el `tipoProcesoOnline`.
            Los componentes de master dispatchan internamente:
            - `CambioButacaPasoZona`: SeccionesComponent cuando
              `tipoSvg === 1` o `tipoSvg === 2 && tipoProcesoOnline === 2`;
              flat zones en otros casos.
            - `CambioButacaPasoButaca`: LeafletComponent cuando
              `tipoSvg === 2` o `tipoSvg === 1 && tipoProcesoOnline === 2`
              (secciones con plazas); SeatSelectionCard (con SVG completo y
              tipo de abono por butaca) en otros casos.

            IMPORTANTE: los pasos de este camino viven dentro de `<keep-alive>`
            y se alternan con `v-if` (NUNCA `v-show`: en Vue 2.7 el `v-show`
            sobre componentes no oculta el root y deja varios pasos
            visibles a la vez). `keep-alive` cachea el paso que se
            desmonta, así `CambioButacaPasoZona` (y su SeccionesComponent)
            conserva la jerarquía de navegación (sección → zona) al pasar
            a PASO_BUTACA y al volver con "Atrás" restaura el nivel de
            selección de zonas de la sección ya elegida en lugar de
            reiniciar en el selector de sectores.

            OJO: la confirmación DEBE vivir dentro de este template (no
            fuera como v-else-if) porque este `<template v-if>` casa por
            camino y se comería los pasos comunes de la cadena externa.
            El pago vive FUERA del keep-alive (iframe de Paycomet no se
            cachea) pero dentro del template.
        -->
        <template v-if="camino === 'secciones'">
            <keep-alive>
                <!-- `tipoSvg` se pasa normalizado desde `iniciar()` (siempre
                     1 o 2 en el camino secciones), por lo que no requiere
                     fallback. Ver `useCambioButaca.iniciar()` JSDoc. -->
                <CambioButacaPasoZona
                    v-if="pasoActual === PASO_ZONA"
                    :tipo-svg="tipoSvg"
                    :tipo-proceso-online="tipoProcesoOnline"
                    :zona-seleccionada="zonaSeleccionada"
                    :renovacion="abonadoEsRenovacion === true"
                    @seleccionar-zona="onSeleccionarZona"
                />

                <CambioButacaPasoButaca
                    v-else-if="pasoActual === PASO_BUTACA"
                    :tipo-svg="tipoSvg"
                    :tipo-proceso-online="tipoProcesoOnline"
                    :asientos-data="asientosData"
                    :svg-string="svgString"
                    :loading-asientos="loadingAsientos"
                    :error-asientos="errorAsientos"
                    :asiento-seleccionado="asientoSeleccionado"
                    :zona-seleccionada="zonaSeleccionada"
                    :precios-disponibles="preciosDisponibles"
                    :tipo-abono-seat-id="tipoAbonoSeatId"
                    :seats-for-picker="asientosParaPicker"
                    @toggle-asiento="toggleAsiento"
                    @quitar-asiento="quitarAsiento"
                    @update:tipo-abono-seat-id="tipoAbonoSeatId = $event"
                />

                <CambioButacaPasoConfirmacion
                    v-else-if="pasoActual === PASO_CONFIRMACION"
                    :zona-seleccionada="zonaSeleccionada"
                    :asiento-seleccionado="asientoSeleccionado"
                    :importe="importe"
                    :importe-cambio="importeCambio"
                    :price-management="priceManagement"
                    :mostrar-gastos-gestion="mostrarGastosGestion"
                    :precio-total="precioTotal"
                    :price-management-interno="priceManagementInterno"
                    :loading="loadingConfirmar"
                    :abono-detail="abonoDetail"
                    :tipo-abono-nuevo-descripcion="tipoAbonoNuevoDescripcion"
                    :jet-id="jetId"
                    :formulario="formulario"
                    :session-id="sessionId"
                    @back="prevStep"
                    @confirmar="handleConfirmar"
                    @continuar-pago="nextStep"
                />
            </keep-alive>

            <!-- Pago: fuera del keep-alive para no cachear el iframe de Paycomet.
                 Se monta SOLO con jetId (Paycomet). El TPV Redsys (`formulario`)
                 se submitea desde la confirmación y NO llega a este paso. -->
            <CambioAsientoPasoPago
                v-if="pasoActual === PASO_PAGO && jetId"
                :jet-id="jetId"
                :session-id="sessionId"
                :precio-total="precioTotal"
                :price-management="priceManagement"
                :mostrar-gastos-gestion="mostrarGastosGestion"
                @back="prevStep"
                @completado="onPagoCompletado"
            />

            <!-- Paso post-pago: confirmación del cambio tras pagar con Paycomet,
                 o tras un cambio gratuito (precioTotal = 0). En ambos casos se
                 muestra el resumen de compra. Fuera del keep-alive. -->
            <CambioButacaPasoConfirmacionPago
                v-else-if="mostrarResumen"
                :abono-detail="abonoDetail"
                :asiento-seleccionado="asientoSeleccionado"
                :zona-seleccionada="zonaSeleccionada"
                :tipo-abono-nuevo-descripcion="tipoAbonoNuevoDescripcion"
                :importe-cambio="importeCambio"
                :price-management="priceManagement"
                :precio-total="precioTotal"
                :mostrar-gastos-gestion="mostrarGastosGestion"
                :pago-resultado="pagoResultado"
                :resultado-cambio="resultadoCambio"
                @completado="onConfirmacionPagoCompletado"
            />
        </template>

        <!-- ── 'regiones' | 'fallback-legacy' | null — flow nuevo ── -->
        <CambioAsientoPasoZona
            v-else-if="pasoActual === PASO_ZONA && (camino === 'regiones' || camino === 'fallback-legacy' || camino === null)"
            :tipo-svg="tipoSvg"
            :svg-recinto="svgRecinto"
            :zonas="zonas"
            :loading-zonas="loadingZonas"
            :zona-seleccionada="zonaSeleccionada"
            @seleccionar-zona="onSeleccionarZona"
        />

        <CambioAsientoPasoRegiones
            v-else-if="pasoActual === PASO_BUTACA && (camino === 'regiones' || camino === 'fallback-legacy' || camino === null)"
            :tipo-svg="tipoSvg"
            :tipo-proceso-online="tipoProcesoOnline"
            :tiene-plazas="Boolean(zonaSeleccionada?.tiene_plazas)"
            :asientos-data="asientosData"
            :svg-string="svgString"
            :loading-asientos="loadingAsientos"
            :error-asientos="errorAsientos"
            :asiento-seleccionado="asientoSeleccionado"
            :zona-seleccionada="zonaSeleccionada"
            :precios-disponibles="preciosDisponibles"
            :tipos-abonos-disponibles="tiposAbonosDisponibles"
            :tipo-abono-seat-id="tipoAbonoSeatId"
            @toggle-asiento="toggleAsiento"
            @quitar-asiento="quitarAsiento"
            @update:tipo-abono-seat-id="tipoAbonoSeatId = $event"
        />

        <!-- ── 'mapa-general' (sin paso de zona) — flow nuevo ── -->
        <CambioAsientoPasoMapaGeneral
            v-else-if="pasoActual === PASO_BUTACA && camino === 'mapa-general'"
            :svg-string="svgString"
            :asientos-data="asientosData"
            :loading-asientos="loadingAsientos"
            :error-asientos="errorAsientos"
            :asiento-seleccionado="asientoSeleccionado"
            :precios-disponibles="preciosDisponibles"
            :tipo-abono-seat-id="tipoAbonoSeatId"
            @toggle-asiento="toggleAsiento"
            @quitar-asiento="quitarAsiento"
            @update:tipo-abono-seat-id="tipoAbonoSeatId = $event"
        />

        <!-- PASO 3: Confirmación -->
        <CambioButacaPasoConfirmacion
            v-else-if="pasoActual === PASO_CONFIRMACION"
            :zona-seleccionada="zonaSeleccionada"
            :asiento-seleccionado="asientoSeleccionado"
            :importe="importe"
            :importe-cambio="importeCambio"
            :price-management="priceManagement"
            :mostrar-gastos-gestion="mostrarGastosGestion"
            :precio-total="precioTotal"
            :price-management-interno="priceManagementInterno"
            :loading="loadingConfirmar"
            :abono-detail="abonoDetail"
            :tipo-abono-nuevo-descripcion="tipoAbonoNuevoDescripcion"
            :jet-id="jetId"
            :formulario="formulario"
            @back="prevStep"
            @confirmar="handleConfirmar"
            @continuar-pago="nextStep"
        />

        <!-- PASO 4: Pago (solo cuando el cambio requiere pasarela Y el backend devolvió
             jet_id (Paycomet). El TPV Redsys (`formulario`) se submitea desde el paso de
             confirmación, así que este paso solo existe con Paycomet. Sin jetId el paso
             no se renderiza. -->
        <CambioAsientoPasoPago
            v-else-if="pasoActual === PASO_PAGO && jetId"
            :jet-id="jetId"
            :session-id="sessionId"
            :precio-total="precioTotal"
            :price-management="priceManagement"
            :mostrar-gastos-gestion="mostrarGastosGestion"
            @back="prevStep"
            @completado="onPagoCompletado"
        />

        <!-- Paso post-pago: confirmación del cambio tras pagar con Paycomet,
             o tras un cambio gratuito (precioTotal = 0). -->
        <CambioButacaPasoConfirmacionPago
            v-else-if="mostrarResumen"
            :abono-detail="abonoDetail"
            :asiento-seleccionado="asientoSeleccionado"
            :zona-seleccionada="zonaSeleccionada"
            :tipo-abono-nuevo-descripcion="tipoAbonoNuevoDescripcion"
            :importe-cambio="importeCambio"
            :price-management="priceManagement"
            :precio-total="precioTotal"
            :mostrar-gastos-gestion="mostrarGastosGestion"
            :pago-resultado="pagoResultado"
            :resultado-cambio="resultadoCambio"
            @completado="onConfirmacionPagoCompletado"
        />
        <!-- ── Botones de navegación (solo en PASO_BUTACA; PASO_ZONA auto-avanza, confirmación tiene los suyos) ── -->
        <div
            v-if="pasoActual === PASO_BUTACA"
            class="tw:flex tw:items-center tw:justify-between tw:gap-3"
        >
            <!-- Botón Atrás: solo cuando hay un paso anterior real. En el camino
                 'mapa-general' el flujo inicia directamente en PASO_BUTACA (no hay
                 PASO_ZONA previo), así que el botón se oculta. -->
            <Button
                v-if="camino !== 'mapa-general'"
                variant="alt"
                :disabled="loadingProcesando || loadingPrevStep"
                :loading="loadingPrevStep"
                @click="prevStep"
            >
                <span class="tw:flex tw:items-center tw:gap-1">
                    <i class="material-icons tw:text-base">arrow_back</i>
                    {{ $t('perfil.cambio_asiento.atras') }}
                </span>
            </Button>

            <!-- Botón Siguiente: ml-auto lo mantiene a la derecha cuando el
                 botón Atrás no se renderiza (camino 'mapa-general'). -->
            <Button
                class="tw:ml-auto"
                variant="alt"
                :disabled="!canAdvance || loadingProcesando"
                :loading="loadingAvanzando"
                @click="handleNext"
            >
                <span class="tw:flex tw:items-center tw:gap-1">
                    {{ $t('perfil.cambio_asiento.siguiente') }}
                    <i class="material-icons tw:text-base">arrow_forward</i>
                </span>
            </Button>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
    import {
        useCambioButaca,
        PASO_ZONA,
        PASO_BUTACA,
        PASO_CONFIRMACION,
        PASO_PAGO,
        PASO_PAGO_CONFIRMACION,
    } from '@/composables/front/perfil/useCambioButaca';
    import { config } from '@/config';
    import store from '@/store';
    import { t } from '@/utils/i18n-helper';
    import { useToast } from '@/composables/common/useToast';
    import { limpiarPagoEnCurso } from '@/utils/pagoEnCurso';
    import CountdownBar from '@/modules/front/ui/CountdownBar.vue';
    import Stepper from '@/modules/front/ui/Stepper.vue';
    import BaseModalFront from '@/modules/front/ui/BaseModalFront.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import CambioAsientoPasoZona from '../components/cambio-asiento/CambioAsientoPasoZona.vue';
    import CambioAsientoPasoRegiones from '../components/cambio-asiento/CambioAsientoPasoRegiones.vue';
    import CambioAsientoPasoMapaGeneral from '../components/cambio-asiento/CambioAsientoPasoMapaGeneral.vue';
    import CambioButacaPasoConfirmacion from '../components/cambio-butaca/CambioButacaPasoConfirmacion.vue';
    import CambioAsientoPasoPago from '../components/cambio-asiento/CambioAsientoPasoPago.vue';
    import CambioButacaPasoConfirmacionPago from '../components/cambio-asiento/CambioButacaPasoConfirmacionPago.vue';
    import CambioButacaPasoZona from '../components/cambio-butaca/CambioButacaPasoZona.vue';
    import CambioButacaPasoButaca from '../components/cambio-butaca/CambioButacaPasoButaca.vue';

    const props = defineProps({
        /** ID del abonado para el que se realiza el cambio de butaca */
        abonoId: {
            type: [String, Number],
            required: true,
        },
    });

    const emit = defineEmits([
        /** Emitido cuando el cambio se completó con éxito. Payload: { cambio_id, n_pedido, session_id } */
        'completado',
        /** Emitido cuando el usuario cancela el flujo o el timer expira. */
        'cancelado',
    ]);    // ─── Orchestator ──────────────────────────────────────────────────────

    const {
        pasoActual,
        tipoSvg,
        tipoProcesoOnline,
        camino,

        zonas,
        loadingZonas,
        errorZonas,
        zonaSeleccionada,
        svgRecinto,

        asientosData,
        svgString,
        asientosParaPicker,
        loadingAsientos,
        errorAsientos,
        asientoSeleccionado,

        sessionId,
        sessionEndsAt,
        tipoAbonoId,
        precioTotal,
        importe,
        importeCambio,
        priceManagement,
        mostrarGastosGestion,
        jetId,
        formulario,
        priceManagementInterno,

        loadingReservar,
        errorReservar,
        loadingConfirmar,
        errorConfirmar,
        abonoDetail,
        abonoDetailError,
        preciosDisponibles,
        tiposAbonosDisponibles,
        tipoAbonoSeatId,
        tipoAbonoNuevoDescripcion,
        resultadoCambio,
        abonadoEsRenovacion,

        puedeAvanzarPasoZona,
        puedeAvanzarPasoButaca,
        puedeConfirmar,

        iniciar,
        seleccionarZona,
        toggleAsiento,
        quitarAsiento,
        prevStep,
        loadingPrevStep,
        nextStep,
        onTimerExpirado,
        confirmarCambio,
        cancelar,
        liberarSesion,
        liberarSesionKeepalive,
        pagoResultado,
        registrarPagoCompletado,
        registrarCambioGratuito,
        requierePago,
    } = useCambioButaca(
        () => props.abonoId,
        () => store.getters.getListaAbonados?.id
    );

    const toast = useToast();

    // ─── Toast para errores del backend ────────────────────────────────────
    watch(errorReservar, val => { if (val) toast.error(val); });
    watch(errorZonas,    val => { if (typeof val === 'string' && val) toast.error(val); });
    watch(errorAsientos, val => { if (typeof val === 'string' && val) toast.error(val); });
    watch(errorConfirmar, val => { if (val) toast.error(val); });
    watch(abonoDetailError, val => { if (val) toast.error(val); });

    // ─── Stepper ───────────────────────────────────────────────────────────

    /**
     * ¿Es necesario mostrar el paso de pago en el stepper?
     * Solo si el cambio requiere pasarela de pago con `jetId` (Paycomet).
     * El TPV Redsys (`formulario`) NO genera paso de pago: se submitea desde
     * la confirmación. El stepper NUNCA debe mostrar "Pago" antes de reservar.
     */
    const requierePagoStep = computed(
        () => precioTotal.value > 0
            && Boolean(jetId.value)
    );

    /**
     * ¿Se muestra el paso de resumen de compra (PASO_PAGO_CONFIRMACION)?
     *
     * Antes solo se alcanzaba tras un pago Paycomet (`jetId` presente), por eso
     * la condición original incluía `&& jetId`. Ahora ese paso también se alcanza
     * en el cambio gratuito (precioTotal === 0) vía `registrarCambioGratuito`.
     * Dado que PASO_PAGO_CONFIRMACION SOLO se asigna desde esos dos caminos
     * (`registrarPagoCompletado` / `registrarCambioGratuito`), basta con comprobar
     * el paso: la condición queda legible y cubre pago + gratuito a la vez.
     */
    const mostrarResumen = computed(
        () => pasoActual.value === PASO_PAGO_CONFIRMACION
    );

    /**
     * Etiquetas de los pasos para el Stepper. Reactivo:
     * - 'mapa-general': 2-4 pasos (butaca, confirmación, [+ pago], resumen de compra).
     * - Resto (incluye flow original master para tipo_svg === 2 y flow nuevo
     *   antifluo): 4-5 pasos (zona, butaca, confirmación, [+ pago], resumen de compra).
     * - Antes de reservar (jetId = null): sin paso de pago.
     * - Después de reservar con jet_id: con paso de pago.
     * - Cambio gratuito (precioTotal = 0): sin paso de pago, pero SÍ con el paso
     *   final "Resumen de compra" (el cambio gratuito también termina en resumen).
     * @type {import('vue').ComputedRef<string[]>}
     */
    const pasoLabels = computed(() => {
        // El paso "butaca" siempre es de selección de butaca: el producto no
        // permite cambiar a zonas sin numerar (with_seats === 0), así que no
        // hay caso en el que el paso se relabele como "tipo de abono".
        const labelPasoButaca = t('perfil.cambio_asiento.paso_butaca', {}, 'Selección de butaca');

        if (camino.value === 'mapa-general') {
            const labels = [
                labelPasoButaca,
                t('perfil.cambio_asiento.paso_confirmacion', {}, 'Confirmación'),
            ];
            if (requierePagoStep.value) {
                labels.push(t('perfil.cambio_asiento.paso_pago', {}, 'Pago'));
            }
            // El resumen de compra es SIEMPRE el último paso (pago o gratuito).
            labels.push(t('perfil.cambio_asiento.paso_confirmacion_pago', {}, 'Resumen de compra'));
            return labels;
        }
        // Flow original master (tipo_svg === 2) y flow nuevo (antifluo/null):
        // pasos (zona, butaca/tipo-abono, confirmación, [+ pago], resumen de compra).
        const labels = [
            t('perfil.cambio_asiento.paso_zona', {}, 'Selección de zona'),
            labelPasoButaca,
            t('perfil.cambio_asiento.paso_confirmacion', {}, 'Confirmación'),
        ];
        if (requierePagoStep.value) {
            labels.push(t('perfil.cambio_asiento.paso_pago', {}, 'Pago'));
        }
        labels.push(t('perfil.cambio_asiento.paso_confirmacion_pago', {}, 'Resumen de compra'));
        return labels;
    });

    /**
     * Índice 1-based para el Stepper. Mapea pasoActual al índice visible.
     * El último paso ("Resumen de compra") existe SIEMPRE; el paso de pago solo
     * suma una posición cuando `requierePagoStep`. Por eso el tope es:
     * - 'mapa-general': PASOS=[butaca, confirmación, (pago), resumen]
     *     conPago=1 → 4 pasos, conPago=0 → 3 pasos.
     *     PASO_BUTACA=1, PASO_CONFIRMACION=2, PASO_PAGO=3, PASO_PAGO_CONFIRMACION=4.
     * - Resto (master Leaflet + flow nuevo antifluo):
     *     PASOS=[zona, butaca, confirmación, (pago), resumen]
     *     conPago=1 → 5 pasos, conPago=0 → 4 pasos.
     *     PASO_ZONA=1, PASO_BUTACA=2, PASO_CONFIRMACION=3, PASO_PAGO=4,
     *     PASO_PAGO_CONFIRMACION=5.
     */
    const stepperStep = computed(() => {
        const conPago = requierePagoStep.value ? 1 : 0;
        if (camino.value === 'mapa-general') {
            return Math.min(pasoActual.value - 1, 3 + conPago);
        }
        return Math.min(pasoActual.value, 4 + conPago);
    });

    // ─── Guards de avance ─────────────────────────────────────────────────

    /** ¿Puede avanzar el usuario desde el paso actual? */
    const canAdvance = computed(() => {
        switch (pasoActual.value) {
            case PASO_ZONA:         return puedeAvanzarPasoZona.value;
            case PASO_BUTACA:       return puedeAvanzarPasoButaca.value;
            default:                return false;
        }
    });

    /** Loading combinado para el botón de siguiente */
    const loadingAvanzando = computed(
        () => loadingReservar.value
    );

    /** Loading que bloquea los botones */
    const loadingProcesando = computed(
        () => loadingReservar.value || loadingConfirmar.value
    );

    // ─── Inicialización ───────────────────────────────────────────────────

    let iniciado = false;

    const iniciarFlujo = async() => {
        if (iniciado) return;
        iniciado = true;

        // Obtener recinto_id, tipo_svg y tipo_proceso_online del store (se setea al cargar la página)
        const recinto = store.getters.getRecinto;
        const recintoId = recinto?.id ?? null;
        const recintoTipoSvg = recinto?.tipo_svg ?? null;
        const recintoTipoProcesoOnline = recinto?.tipo_proceso_online ?? null;
        await iniciar(recintoId, recintoTipoSvg, recintoTipoProcesoOnline);
    };

    // ── Liberar sesión al recargar/cerrar pestaña ──────────────────────────
    // El HTTP con `keepalive: true` lo gestiona `useCambioButaca` para que el
    // view no manipule tokens/headers directamente. Aquí solo delegamos.
    const onBeforeUnload = () => {
        if (!sessionId.value) return;
        liberarSesionKeepalive(sessionId.value);
    };

    onMounted(() => {
        window.scrollTo(0, 0);
        // Si la SPA se remonta tras volver de la pasarela APM (Bizum), el
        // pago ya terminó (o el usuario volvió sin pagar): el flag de pago
        // en curso ya no debe proteger la sesión.
        limpiarPagoEnCurso();
        window.addEventListener('beforeunload', onBeforeUnload);
        iniciarFlujo();
    });

    onUnmounted(() => {
        window.removeEventListener('beforeunload', onBeforeUnload);
        if (sessionId.value) {
            cancelar();
        }
    });

    // ─── Timer ────────────────────────────────────────────────────────────

    const showModalTiempoAgotado = ref(false);

    const handleTimerExpirado = async() => {
        await onTimerExpirado();
        showModalTiempoAgotado.value = true;
    };

    const cerrarModalTiempoAgotado = () => {
        showModalTiempoAgotado.value = false;
        emit('cancelado');
    };

    // ─── Selección de zona con auto-avance ─────────────────────────────────

    const onSeleccionarZona = async zona => {
        const result = seleccionarZona(zona);
        if (result && pasoActual.value === PASO_ZONA) {
            // En todos los flows (master secciones + nuevo regiones /
            // fallback-legacy / null) el avance a PASO_BUTACA dispara
            // `cargarAsientos`.
            await nextStep();
        }
    };

    // ─── Navegación ───────────────────────────────────────────────────────

    const handleNext = async() => {
        await nextStep();
    };

    // ─── Confirmación ─────────────────────────────────────────────────────

    /**
     * Confirmación del cambio desde el paso de confirmación.
     *
     * El botón "Confirmar cambio" solo aparece cuando `precioTotal === 0`
     * (gratuito); con pago el botón es "Continuar al pago" (`nextStep`), así
     * que este handler gestiona principalmente el caso gratuito.
     *
     * - Cambio gratuito (precioTotal === 0, sin requierePago): en lugar de
     *   emitir `completado` y cerrar el flujo, avanzamos a PASO_PAGO_CONFIRMACION
     *   para mostrar el resumen de compra. El cierre real lo hace el botón
     *   "Finalizar" de ese paso (`onConfirmacionPagoCompletado`), que emite
     *   el mismo payload al padre.
     * - Cambio con pago: se mantiene el comportamiento previo (emit directo).
     */
    const handleConfirmar = async() => {
        const success = await confirmarCambio();
        if (success && resultadoCambio.value) {
            if (requierePago.value) {
                emit('completado', resultadoCambio.value);
            } else {
                registrarCambioGratuito();
            }
        }
    };

    /**
     * Pago Paycomet completado. PagoPaycometComponent emite `completado`
     * con la respuesta de `get-pedido-procesado-paycomet` (y la redirección
     * está desactivada vía `no-redirigir`). En lugar de cerrar el flujo,
     * guardamos el resultado del pago y avanzamos al paso post-pago
     * (PASO_PAGO_CONFIRMACION). El cierre real lo hace el botón "Finalizar"
     * de ese paso (`onConfirmacionPagoCompletado`).
     */
    const onPagoCompletado = result => {
        registrarPagoCompletado(result);
    };

    /**
     * Botón "Finalizar" del paso post-pago: cierra el flujo emitiendo
     * `completado` al padre (PerfilAbonosView) con el resultado del cambio
     * y el pago. La redirección ya no se usa, así que el toast de éxito se
     * muestra UNA sola vez aquí (no hay entrada por query).
     *
     * También se usa en el cambio GRATUITO: `pagoResultado` queda en `null`
     * (`registrarCambioGratuito`), así que se emite `{ ...resultadoCambio,
     * pago: null }` — payload válido para el padre.
     */
    const onConfirmacionPagoCompletado = () => {
        emit('completado', {
            ...resultadoCambio.value,
            pago: pagoResultado.value,
        });
    };

</script>
